<script setup lang="ts" name="FieldRichTextEditor">
import { computed, ref } from 'vue';
import { QuillEditor } from '@vueup/vue-quill';
import { useVModel } from '@vueuse/core';

import FieldBaseErrors from '~/components/FieldBaseErrors.vue';

import type { IFormField } from '~/types';

import '@vueup/vue-quill/dist/vue-quill.snow.css';

const props = withDefaults(
  defineProps<{
    field: IFormField;
    modelValue: string;
    errors?: string[];
    hasErrors?: boolean;
  }>(),
  {
    errors: (): string[] => [],
  },
);

const emit = defineEmits<{
  'update:modelValue': [string];
}>();

const inputValue = useVModel(props, 'modelValue', emit);

const options = computed(() => ({
  theme: '',
  modules: {
    toolbar: '#my-toolbar',
  },
  placeholder: props.field.placeholder || 'Type your message...',
}));

const allErrors = computed(() => props.errors || []);
</script>

<template>
  <div id="rich-text-editor" :data-test="`${field.name}-wrapper`">
    <div class="max-w-[580px] rounded-s bg-charcoal-0 text-charcoal-9">
      <div
        class="h-[180px] min-h-[190px] overflow-hidden rounded-t-lg border border-charcoal-5 text-base leading-4 ring-inset focus-within:border-0 focus-within:p-[1px] focus-within:ring-2 focus-within:ring-primary-6 hover:border-charcoal-6"
      >
        <QuillEditor
          v-model:content="inputValue"
          :options="options"
          content-type="html"
        >
        </QuillEditor>
      </div>
      <div
        id="my-toolbar"
        class="rounded-b-lg !border-x !border-b !border-t-0 !border-charcoal-5 bg-charcoal-1"
      >
        <button class="ql-bold !text-charcoal-2"></button>
        <button class="ql-italic"></button>
        <button class="ql-underline"></button>
      </div>
    </div>

    <FieldBaseErrors
      v-if="allErrors.length > 0"
      class="mt-1"
      :errors="allErrors"
      :data-test="`${field.name}-error`"
    />
  </div>
</template>

<style scoped>
#rich-text-editor ::v-deep(.ql-container) {
  font-family: inherit;
  border: none;
  font-size: inherit;
}
#rich-text-editor ::v-deep(.ql-container .ql-editor) {
  line-height: inherit;
}

#rich-text-editor ::v-deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: theme('colors.charcoal.5');
}

#my-toolbar ::v-deep(.ql-active) {
  background-color: theme('colors.charcoal.2');
}

#my-toolbar ::v-deep(.ql-stroke, ) {
  stroke: theme('colors.charcoal.9');
}
#my-toolbar ::v-deep(.ql-fill) {
  fill: theme('colors.charcoal.9');
}
</style>
